import React, { createContext, useContext, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import useRFIDetails from "../hooks/useRFIDetails";
import useRFIStages from "../hooks/useRFIStages";

// Create Context
const RfiContext = createContext();

/**
 * RfiProvider - Enhanced context provider for RFI data management
 * Manages RFI details, stages, and all stage-specific data (blasting, painting coats)
 * Replaces Redux for stages data management
 */
export const RfiProvider = ({ children }) => {
  const { rfinumber } = useParams();
  const { rfiDetails, loading, getRFIDetails, skipStage } = useRFIDetails({
    rfi_id: rfinumber,
  });

  // ============================================================================
  // STAGES DATA STATE - Simple data storage without loading/error complexity
  // ============================================================================

  // Dimensional stage
  const [dimensionalData, setDimensionalData] = useState({
    data: null,
  });
  const [assemblyData, setAssemblyDataContext] = useState(null);

  const [blastingData, setBlastingData] = useState(null);
  const [primerData, setPrimerData] = useState(null);
  const [secondcoatData, setSecondCoatData] = useState(null);
  const [topcoatData, setTopCoatData] = useState(null);
  const [additionsData, setAdditionsData] = useState(null);
  const [stagesReviewData, setStagesReviewData] = useState([]);

  // No additional methods needed - useState setters are already memoized

  // ============================================================================
  // RFI STAGES HOOK - Called after setStagesReviewData is defined
  // ============================================================================

  const {
    stagesDetails,
    loading: stagesLoading,
    getRFIStages,
    submitStageAPI,
  } = useRFIStages({
    rfi_id: rfinumber,
    setStagesReviewData, // Pass the setter to avoid circular dependency
  });

  // ============================================================================
  // CONTEXT VALUE - Memoized to prevent unnecessary re-renders
  // ============================================================================

  const value = useMemo(
    () => ({
      // Existing RFI context values
      rfiDetails,
      loading,
      getRFIDetails,
      skipStage,
      stagesDetails,
      stagesLoading,
      getRFIStages,
      submitStageAPI,

      // Stages data - simplified structure
      stagesData: {
        dimensional: dimensionalData,
        assembly: { data: assemblyData },
        blasting: { data: blastingData },
        primer: { data: primerData },
        secondcoat: { data: secondcoatData },
        topcoat: { data: topcoatData },
        additions: { data: additionsData },
        stagesReview: { data: stagesReviewData },
      },

      // Stage data setters
      setBlastingData,
      setDimensionalData,
      setAssemblyDataContext,
      setPrimerData,
      setSecondCoatData,
      setTopCoatData,
      setAdditionsData,
      setStagesReviewData,
    }),
    [
      rfiDetails,
      dimensionalData,
      assemblyData,
      loading,
      getRFIDetails,
      skipStage,
      stagesDetails,
      stagesLoading,
      getRFIStages,
      submitStageAPI,
      blastingData,
      primerData,
      secondcoatData,
      topcoatData,
      additionsData,
      stagesReviewData,
    ]
  );

  return <RfiContext.Provider value={value}>{children}</RfiContext.Provider>;
};

// Custom hook to use the context
export const useRfi = () => {
  const context = useContext(RfiContext);
  if (!context) {
    throw new Error("useRfi must be used within an RfiProvider");
  }
  return context;
};

export default RfiContext;
